// ===== CompteForm.js =====
import React, { useState, useEffect } from 'react';

const CompteForm = ({ compte, classes, rubriques, comptes, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    code: '',
    niveau: 3,
    libelle: '',
    classe_id: '',
    rubrique_id: '',
    parent_id: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [filteredRubriques, setFilteredRubriques] = useState([]);
  const [filteredParents, setFilteredParents] = useState([]);

  useEffect(() => {
    if (compte) {
      setFormData({
        code: compte.code || '',
        niveau: compte.niveau || 3,
        libelle: compte.libelle || '',
        classe_id: compte.classe_id?._id || '',
        rubrique_id: compte.rubrique_id?._id || '',
        parent_id: compte.parent_id?._id || '',
        description: compte.description || ''
      });
    }
  }, [compte]);

  // Filtrer les rubriques selon la classe sélectionnée
  useEffect(() => {
    if (formData.classe_id) {
      const filtered = rubriques.filter(r => r.classe_id._id === formData.classe_id);
      setFilteredRubriques(filtered);
    } else {
      setFilteredRubriques([]);
    }
  }, [formData.classe_id, rubriques]);

  // Filtrer les comptes parents selon la classe sélectionnée
  useEffect(() => {
    if (formData.classe_id) {
      const filtered = comptes.filter(c => 
        c.classe_id._id === formData.classe_id && 
        c.niveau < formData.niveau &&
        c._id !== compte?._id // Exclure le compte lui-même
      );
      setFilteredParents(filtered);
    } else {
      setFilteredParents([]);
    }
  }, [formData.classe_id, formData.niveau, comptes, compte]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Le code est obligatoire';
    } else if (formData.code.length < 1 || formData.code.length > 10) {
      newErrors.code = 'Le code doit faire entre 1 et 10 caractères';
    }

    if (!formData.niveau || formData.niveau < 1 || formData.niveau > 5) {
      newErrors.niveau = 'Le niveau doit être entre 1 et 5';
    }

    if (!formData.libelle.trim()) {
      newErrors.libelle = 'Le libellé est obligatoire';
    } else if (formData.libelle.length > 300) {
      newErrors.libelle = 'Le libellé ne peut pas dépasser 300 caractères';
    }

    if (!formData.classe_id) {
      newErrors.classe_id = 'La classe est obligatoire';
    }

    // Vérifier que le code commence par le code de la classe sélectionnée
    if (formData.code && formData.classe_id) {
      const selectedClasse = classes.find(c => c._id === formData.classe_id);
      if (selectedClasse && !formData.code.startsWith(selectedClasse.code)) {
        newErrors.code = `Le code doit commencer par ${selectedClasse.code}`;
      }
    }

    // Vérifier la cohérence parent-enfant
    if (formData.parent_id) {
      const parent = comptes.find(c => c._id === formData.parent_id);
      if (parent && formData.niveau <= parent.niveau) {
        newErrors.niveau = 'Le niveau doit être supérieur au compte parent';
      }
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'La description ne peut pas dépasser 500 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Nettoyer les champs vides
      const cleanData = {
        ...formData,
        rubrique_id: formData.rubrique_id || null,
        parent_id: formData.parent_id || null
      };
      onSave(cleanData);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'niveau' ? parseInt(value) : value
    }));
    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleClasseChange = (e) => {
    const selectedClasseId = e.target.value;
    const selectedClasse = classes.find(c => c._id === selectedClasseId);
    
    setFormData(prev => ({
      ...prev,
      classe_id: selectedClasseId,
      rubrique_id: '', // Reset rubrique
      parent_id: '', // Reset parent
      // Si c'est un nouveau compte, préfixer le code avec le code de classe
      code: !compte && selectedClasse ? selectedClasse.code + (prev.code.length > 1 ? prev.code.substring(1) : '') : prev.code
    }));
    
    if (errors.classe_id) {
      setErrors(prev => ({
        ...prev,
        classe_id: ''
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Classe */}
      <div>
        <label htmlFor="classe_id" className="block text-sm font-medium text-gray-700">
          Classe *
        </label>
        <select
          id="classe_id"
          name="classe_id"
          value={formData.classe_id}
          onChange={handleClasseChange}
          disabled={!!compte} // La classe ne peut pas être modifiée
          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${
            errors.classe_id ? 'border-red-300' : ''
          } ${compte ? 'bg-gray-100' : ''}`}
        >
          <option value="">Sélectionner une classe...</option>
          {classes.map(classe => (
            <option key={classe._id} value={classe._id}>
              Classe {classe.code} - {classe.libelle}
            </option>
          ))}
        </select>
        {errors.classe_id && <p className="mt-1 text-sm text-red-600">{errors.classe_id}</p>}
        {compte && (
          <p className="mt-1 text-sm text-gray-500">La classe ne peut pas être modifiée</p>
        )}
      </div>

      {/* Code et Niveau sur la même ligne */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="code" className="block text-sm font-medium text-gray-700">
            Code compte *
          </label>
          <input
            type="text"
            id="code"
            name="code"
            value={formData.code}
            onChange={handleChange}
            disabled={!!compte} // Le code ne peut pas être modifié
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${
              errors.code ? 'border-red-300' : ''
            } ${compte ? 'bg-gray-100' : ''}`}
            placeholder="Ex: 101, 1011..."
            maxLength={10}
          />
          {errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
        </div>

        <div>
          <label htmlFor="niveau" className="block text-sm font-medium text-gray-700">
            Niveau *
          </label>
          <select
            id="niveau"
            name="niveau"
            value={formData.niveau}
            onChange={handleChange}
            disabled={!!compte} // Le niveau ne peut pas être modifié
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${
              errors.niveau ? 'border-red-300' : ''
            } ${compte ? 'bg-gray-100' : ''}`}
          >
            {[1, 2, 3, 4, 5].map(niveau => (
              <option key={niveau} value={niveau}>
                Niveau {niveau}
              </option>
            ))}
          </select>
          {errors.niveau && <p className="mt-1 text-sm text-red-600">{errors.niveau}</p>}
        </div>
      </div>

      {/* Libellé */}
      <div>
        <label htmlFor="libelle" className="block text-sm font-medium text-gray-700">
          Libellé *
        </label>
        <input
          type="text"
          id="libelle"
          name="libelle"
          value={formData.libelle}
          onChange={handleChange}
          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${
            errors.libelle ? 'border-red-300' : ''
          }`}
          placeholder="Ex: Capital social"
          maxLength={300}
        />
        {errors.libelle && <p className="mt-1 text-sm text-red-600">{errors.libelle}</p>}
        <p className="mt-1 text-sm text-gray-500">
          {formData.libelle.length}/300 caractères
        </p>
      </div>

      {/* Rubrique (optionnelle) */}
      <div>
        <label htmlFor="rubrique_id" className="block text-sm font-medium text-gray-700">
          Rubrique (optionnelle)
        </label>
        <select
          id="rubrique_id"
          name="rubrique_id"
          value={formData.rubrique_id}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          disabled={!formData.classe_id}
        >
          <option value="">Aucune rubrique</option>
          {filteredRubriques.map(rubrique => (
            <option key={rubrique._id} value={rubrique._id}>
              {rubrique.code} - {rubrique.libelle}
            </option>
          ))}
        </select>
        {!formData.classe_id && (
          <p className="mt-1 text-sm text-gray-500">Sélectionnez d'abord une classe</p>
        )}
      </div>

      {/* Compte parent (optionnel) */}
      <div>
        <label htmlFor="parent_id" className="block text-sm font-medium text-gray-700">
          Compte parent (optionnel)
        </label>
        <select
          id="parent_id"
          name="parent_id"
          value={formData.parent_id}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          disabled={!formData.classe_id}
        >
          <option value="">Aucun parent</option>
          {filteredParents.map(parent => (
            <option key={parent._id} value={parent._id}>
              {parent.code} - {parent.libelle} (Niveau {parent.niveau})
            </option>
          ))}
        </select>
        {!formData.classe_id && (
          <p className="mt-1 text-sm text-gray-500">Sélectionnez d'abord une classe</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${
            errors.description ? 'border-red-300' : ''
          }`}
          placeholder="Description optionnelle du compte..."
          maxLength={500}
        />
        {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
        <p className="mt-1 text-sm text-gray-500">
          {formData.description.length}/500 caractères
        </p>
      </div>

      {/* Boutons */}
      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          disabled={loading}
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Enregistrement...' : compte ? 'Modifier' : 'Créer'}
        </button>
      </div>
    </form>
  );
};

// Exports
export default CompteForm;